import store from 'redux/store';
import { deleteUser } from 'redux/slices/userSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditUser } from './edit/showEditUser';

type UserEntity = Extract<ActionEntity, { type: 'user' }>;

const isAllUser = (entities: readonly ActionEntity[]): entities is readonly UserEntity[] => entities.every((entity) => entity.type === 'user');

/**
 * Action definitions for users. `createUser` / `addToTransportUser` are scoped;
 * `edit` (single-only) and `delete` are contextual. delete now confirms via the
 * shared executor (was a window.confirm).
 */
export const userActionDefinitions: ActionDefinitions = [
  {
    id: 'createUser',
    scope: 'type',
    label: 'New User',
    resolve: () => () => showEditUser(null),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllUser(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => showEditUser(only.id);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllUser(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteUser(entity.id)), {
        refresh,
        confirmBody: 'Are you sure you want to delete this user?',
      });
    },
  },
  {
    id: 'addToTransportUser',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllUser(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'user',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.name : 'Selected users' }
      );
    },
  },
];
